import { ConfirmModal } from '@/shared/ui';
import EcgViewerWrapper from '../ecgViewerWrapper';
import HrChart from '../hrChart';
import HrChartOptions from '../hrChartOptions';
import SelectedDisclosure from '../selectedDisclosure';
import useEcgDisclosure from './useEcgDisclosure';

export type EcgDisclosureProps = {
  isActive?: boolean;
  ecgDataMap: any;
  timezoneOffset?: any;
  studyId?: any;
  profileId?: any;
  startReport?: any;
  stopReport?: any;
};

/**
 * ECG disclosure view combining the selected-disclosure summary, the HR chart and its options bar with the full ECG viewer. useEcgDisclosure supplies the chart refs and the empty-data flag that swaps the chart area for a no-data message, and raises an invalid-time confirmation when the chosen range crosses a no-data gap; the whole view is mounted only while the tab is active or on its first render.
 *
 * @param props - View props carrying the study and profile ids, report window, timezone offset, ECG data map and the tab-active flag.
 * @returns The rendered ECG disclosure layout, or false while the tab has never been active.
 */
const EcgDisclosure = ({
  isActive,
  ecgDataMap,
  timezoneOffset,
  studyId,
  profileId,
  startReport,
  stopReport,
}: EcgDisclosureProps) => {
  const {
    isEmptyData,
    isOpenInvalidTimeModal,
    onClickOkay,
    handleClickArrowButton,
    hrChartRef,
    hrChartOptionsRef,
    isInitial,
  } = useEcgDisclosure({ isActive });

  return (
    (isActive || isInitial.current) && (
      <>
        <ConfirmModal
          isOpen={isOpenInvalidTimeModal}
          onClickRightButton={onClickOkay}
          rightButtonName="Okay"
          title="Invalid time"
          question="Please try a different time range because the selected time contains the no-data part."
          isRedBtn={false}
        />

        <div className="ecg-disclosure-container">
          <SelectedDisclosure timezoneOffset={timezoneOffset} />
          <HrChartOptions
            ref={hrChartOptionsRef}
            timezoneOffset={timezoneOffset}
            studyId={studyId}
            profileId={profileId}
            startReport={startReport}
            stopReport={stopReport}
          />
          {isEmptyData ? (
            <div className="empty-data-container">
              <div className="empty-data-text">There is no data recorded</div>
            </div>
          ) : (
            <>
              <HrChart
                ref={hrChartRef}
                ecgDataMap={ecgDataMap}
                timezoneOffset={timezoneOffset}
                studyId={studyId}
                profileId={profileId}
              />
              <EcgViewerWrapper
                ecgDataMap={ecgDataMap}
                isActive={isActive}
                timezoneOffset={timezoneOffset}
                profileId={profileId}
                studyId={studyId}
                handleClickArrowButton={handleClickArrowButton}
              />
            </>
          )}
        </div>
      </>
    )
  );
};

export default EcgDisclosure;
